// src/features/links/LinkTypePie.tsx
// IR-1386 r3 (Rotem): "add pie chart for link types - same line as filter -
// similar design to avatr tab". Reuses the Avatars-tab ring (DonutWithLegend)
// so the two screens read as one system.
//
// It also answers the question she asked in the same comment — "if the system
// discards them, what is displayed?". The NoiseNotice line underneath says what
// is held back; this says what is LEFT, and the two numbers add up on screen.
import { DonutWithLegend } from "@/features/statistics/chart-primitives";
import type { ReactNode } from "react";
import { TARGET_KIND_LABELS } from "./TargetKindBadge";
import type { TargetKind } from "./types";

/**
 * Fixed colour per type — NOT the palette-by-index default.
 *
 * The slice set changes as the analyst filters (asking for Telegram alone drops
 * every WhatsApp-only type), and an index-keyed palette would recolour the
 * surviving slices each time. A reader who has just learnt "pink = private
 * group" would watch it become the colour of something else mid-session.
 */
const TYPE_COLOR: Record<string, string> = {
  "Private group": "#ff2ea6", // --iris-neon-magenta // token-exempt
  "Public group": "#a855f7", // --iris-neon-purple // token-exempt
  "Private channel": "#22d3ff", // --iris-neon-cyan // token-exempt
  "Public channel": "#3e8dff", // --iris-neon-blue // token-exempt
  User: "#ff9933", // --iris-neon-orange // token-exempt
  Bot: "#eaff2a", // --iris-neon-yellow // token-exempt
  Unknown: "#6b7184", // --iris-fg-4 // token-exempt
};

/** Stable slice order, so the ring doesn't reshuffle between renders. */
const ORDER: TargetKind[] = [
  "private_group",
  "public_channel",
  "public_group",
  "private_channel",
  "user",
  "bot",
  "unknown",
];

export function linkTypeSlices(counts: Partial<Record<TargetKind, number>> | null) {
  if (!counts) return [];
  return ORDER.filter((k) => (counts[k] ?? 0) > 0).map((k) => ({
    label: TARGET_KIND_LABELS[k],
    count: counts[k] ?? 0,
  }));
}

interface Props {
  counts: Partial<Record<TargetKind, number>> | null;
  /**
   * IR-1386 r4 — rendered under the ring, inside the same box (the "By
   * platform" counts). One box, because both facets answer the same question —
   * what the queue currently in front of you is made of — and both follow the
   * filters identically. Two boxes would imply two scopes.
   */
  footer?: ReactNode;
}

export function LinkTypePie({ counts, footer }: Props) {
  const data = linkTypeSlices(counts);

  // The backend facet ships after this UI (the FE deploy must be inert until
  // then), and a filter can legitimately select nothing. Render nothing rather
  // than an empty ring, which reads as a broken chart — the table beside it
  // already states the count.
  if (data.length === 0 && !footer) return null;

  return (
    <div
      data-testid="link-type-pie"
      className="rounded border border-[var(--iris-line-2)] p-3"
      style={{ flex: "0 0 300px", display: "flex", flexDirection: "column", gap: 6 }}
      title="What the links currently in the list are made of — what they point at, and which platform they came from. Follows the filters, so every number here is in the table beside it. Personal accounts, bots and dead links are held back by default and only appear once you ask for them."
    >
      {data.length > 0 && (
        <>
          <span className="text-xs font-medium text-[var(--iris-fg-3)]">By type</span>
          <div style={{ flex: 1, minHeight: 116 }}>
            <DonutWithLegend
              data={data}
              nameKey="label"
              dataKey="count"
              size={116}
              totalLabel="Links"
              colorFor={(name) => TYPE_COLOR[name]}
            />
          </div>
        </>
      )}
      {footer && (
        <div className={data.length > 0 ? "border-t border-[var(--iris-line-2)] pt-2" : undefined}>
          {footer}
        </div>
      )}
    </div>
  );
}
